import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import { getSizeRangesMasterDetails, deleteSizeRangeMaster, deleteSizeInRange, bulkSaveSizeRangesMaster } from './Store/SizeRangeMaster.action';
import './SystemMasters.css';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';

function POSizeMaster() {
  const dispatch = useDispatch();
  const reducer = useSelector((estate) => estate);
  const {
    SystemMastersDetails,
    UserDetails,
    Users: { logInRights },
  } = reducer;

  const { sizeRangesMasterList } = SystemMastersDetails;
  const { userInfo } = UserDetails;
  const loggedInUserId = userInfo.userId;
  const { t } = useTranslation('systemMaster');

  const normalizedSizeRangesMasterList = useMemo(() => {
    if (Array.isArray(sizeRangesMasterList)) return sizeRangesMasterList;
    if (Array.isArray(sizeRangesMasterList?.data)) return sizeRangesMasterList.data;
    return [];
  }, [sizeRangesMasterList]);

  const { CheckActionPermission } = useComponent();
  const [userRights, setUserRights] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [buttonDown, setButtonDown] = useState(true);
  const [rangesData, setRangesData] = useState([]);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [expandedRangeIds, setExpandedRangeIds] = useState({});

  const [showDeleteRangeConfirm, setShowDeleteRangeConfirm] = useState(false);
  const [showDeleteSizeConfirm, setShowDeleteSizeConfirm] = useState(false);
  const [pendingDeleteRangeId, setPendingDeleteRangeId] = useState(null);
  const [pendingDeleteSize, setPendingDeleteSize] = useState({
    sizeRangeId: null,
    sizeId: null,
  });
  const [isLoadingConf, setIsLoadingConf] = useState(false);

  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights);
    }
  }, [logInRights]);

  const masterAddEditAccess = CheckActionPermission(userRights, 'masters', 'poSize', 'addEdit');
  const masterDeleteAccess = CheckActionPermission(userRights, 'masters', 'poSize', 'delete');

  const SIZE_RANGE_NAME_LIMIT = 30;
  const SIZE_NAME_LIMIT = 30;

  const tempRangeObj = useMemo(
    () => ({
      name: '',
      _id: '',
      isNew: true,
      sizes: [],
    }),
    [],
  );

  const tempSizeObj = useMemo(
    () => ({
      name: '',
      _id: '',
      isNew: true,
    }),
    [],
  );

  useEffect(() => {
    setRangesData(normalizedSizeRangesMasterList);
  }, [normalizedSizeRangesMasterList]);

  const fetchRanges = useCallback(async () => {
    await dispatch(getSizeRangesMasterDetails({ loggedInUserId }));
  }, [dispatch, loggedInUserId]);

  const callApi = async () => {
    setButtonDown(!buttonDown);
    setIsLoading(true);
    if (buttonDown) setShowPanel(true);
    else setShowPanel(false);
    if (isEmpty(sizeRangesMasterList)) {
      await fetchRanges();
    }
    setIsLoading(false);
  };

  const isDuplicateSizeName = (range, sizeIndex, name) => {
    const val = name?.toString()?.trim()?.toLowerCase();
    if (!val) return false;
    const sizes = Array.isArray(range?.sizes) ? range.sizes : [];
    return sizes.some((s, idx) => idx !== sizeIndex && (s?.name ?? '').toString().trim().toLowerCase() === val);
  };

  const isDuplicateRangeName = (prevRanges, rangeIndex, name) => {
    const val = name?.toString()?.trim()?.toLowerCase();
    if (!val) return false;
    return prevRanges.some((r, idx) => idx !== rangeIndex && (r?.name ?? '').toString().trim().toLowerCase() === val);
  };

  const handleSaveAll = async () => {
    if (isSavingAll) return;

    const rangesToSave = rangesData || [];
    if (rangesToSave.length === 0) {
      toasterMsg(t('SIZE_RANGE_MASTER.VALIDATION.NO_RANGES_TO_SAVE') || 'No size ranges to save', 'warning');
      return;
    }

    setIsSavingAll(true);

    try {
      rangesToSave.forEach((range, rangeIndex) => {
        const rangeName = range?.name?.toString()?.trim();
        if (rangeName && isDuplicateRangeName(rangesToSave, rangeIndex, rangeName)) {
          throw new Error(t('SIZE_RANGE_MASTER.VALIDATION.RANGE_NAME_DUPLICATE') || 'Size range name should be unique');
        }
      });

      const payload = {
        sizeRanges: rangesToSave.map((range) => {
          const rangeName = range?.name?.toString()?.trim();

          if (!rangeName) {
            throw new Error(t('SIZE_RANGE_MASTER.VALIDATION.RANGE_NAME_REQUIRED') || 'Range name is required');
          }

          const sizes = Array.isArray(range?.sizes) ? range.sizes : [];
          const sizesToSave = sizes
            .map((size, sizeIndex) => {
              const sizeName = size?.name?.toString()?.trim();
              if (!sizeName) {
                throw new Error(t('SIZE_RANGE_MASTER.VALIDATION.SIZE_NAME_REQUIRED') || 'Size name is required');
              }

              if (isDuplicateSizeName(range, sizeIndex, sizeName)) {
                throw new Error(t('SIZE_RANGE_MASTER.VALIDATION.SIZE_NAME_DUPLICATE') || 'Duplicate size name');
              }

              return {
                _id: size?._id && !size?.isNew ? size._id : null,
                name: sizeName,
              };
            })
            .filter(Boolean);

          return {
            _id: range?._id && !range?.isNew ? range._id : null,
            name: rangeName,
            sizes: sizesToSave,
          };
        }),
        loggedInUserId,
      };

      await dispatch(bulkSaveSizeRangesMaster(payload, t));
      await fetchRanges();
    } catch (error) {
      if (error.message) {
        toasterMsg(error.message, 'danger');
      } else {
        toasterMsg(t('SIZE_RANGE_MASTER.SAVE_ERROR') || 'Failed to save size ranges', 'danger');
      }
    } finally {
      setIsSavingAll(false);
    }
  };

  const handleAddRange = async () => {
    if (!showPanel) {
      setButtonDown(!buttonDown);
      setIsLoading(true);
      setShowPanel(true);
      if (isEmpty(sizeRangesMasterList)) {
        await fetchRanges();
      }
      setIsLoading(false);
    }
    setRangesData((prev) => (isEmpty(prev) ? [tempRangeObj] : [...prev, tempRangeObj]));
  };

  const handleRangeNameChange = (rangeIndex, value) => {
    if (value.length <= SIZE_RANGE_NAME_LIMIT) {
      setRangesData((prev) => {
        const clone = [...prev];
        clone[rangeIndex] = { ...clone[rangeIndex], name: value };
        return clone;
      });
    }
  };

  const handleDeleteRange = (range, rangeIndex) => {
    const sizeRangeId = range?._id;
    if (!sizeRangeId || sizeRangeId === '' || range?.isNew) {
      setRangesData((prev) => prev.filter((_, idx) => idx !== rangeIndex));
      return;
    }
    setPendingDeleteRangeId(sizeRangeId);
    setShowDeleteRangeConfirm(true);
  };

  const deleteRangeConfirmed = async () => {
    if (!pendingDeleteRangeId) return;
    setIsLoadingConf(true);
    await dispatch(deleteSizeRangeMaster(pendingDeleteRangeId, { loggedInUserId }, t));
    setIsLoadingConf(false);
    setShowDeleteRangeConfirm(false);
    setPendingDeleteRangeId(null);
  };

  const toggleRange = (rangeId) => {
    setExpandedRangeIds((prev) => ({ ...prev, [rangeId]: !prev[rangeId] }));
  };

  const handleAddSize = (rangeIndex) => {
    setRangesData((prev) => {
      const clone = [...prev];
      const range = clone[rangeIndex];
      const sizes = Array.isArray(range?.sizes) ? [...range.sizes] : [];
      sizes.push(tempSizeObj);
      clone[rangeIndex] = { ...range, sizes };
      return clone;
    });
    const rangeId = rangesData?.[rangeIndex]?._id || `new_${rangeIndex}`;
    setExpandedRangeIds((prev) => ({ ...prev, [rangeId]: true }));
  };

  const handleSizeNameChange = (rangeIndex, sizeIndex, value) => {
    if (value.length <= SIZE_NAME_LIMIT) {
      setRangesData((prev) => {
        const clone = [...prev];
        const range = clone[rangeIndex];
        const sizes = Array.isArray(range?.sizes) ? [...range.sizes] : [];
        sizes[sizeIndex] = { ...sizes[sizeIndex], name: value };
        clone[rangeIndex] = { ...range, sizes };
        return clone;
      });
    }
  };

  const handleDeleteSize = (sizeRangeId, sizeId, rangeIndex, sizeIndex) => {
    if (!sizeId) {
      setRangesData((prev) => {
        const clone = [...prev];
        const range = clone[rangeIndex];
        const sizes = Array.isArray(range?.sizes) ? [...range.sizes] : [];
        sizes.splice(sizeIndex, 1);
        clone[rangeIndex] = { ...range, sizes };
        return clone;
      });
      return;
    }
    setPendingDeleteSize({ sizeRangeId, sizeId });
    setShowDeleteSizeConfirm(true);
  };

  const deleteSizeConfirmed = async () => {
    const { sizeRangeId, sizeId } = pendingDeleteSize || {};
    if (!sizeRangeId || !sizeId) return;
    setIsLoadingConf(true);
    await dispatch(deleteSizeInRange(sizeRangeId, sizeId, { loggedInUserId }, t));
    setIsLoadingConf(false);
    setShowDeleteSizeConfirm(false);
    setPendingDeleteSize({ sizeRangeId: null, sizeId: null });
  };

  return (
    <Accordion className="mb-4 master-accordian">
      <div className="card">
        <div className="card-header">
          <div className="title-container">
            <p className="font-heavy">{t('SIZE_RANGE_MASTER.TITLE')}</p>
          </div>
          <div className="action-title-btns">
            <button
              type="button"
              className="btn add-size-range p-0"
              onClick={handleAddRange}
              disabled={!masterAddEditAccess}
              aria-label={t('COMMON_LABEL.ADD')}
            >
              <i className="icon-Add-bold f-18" />
            </button>
            <button type="button" className="btn d-flex p-0 ml-16" onClick={callApi} aria-label="toggle">
              <i className={!buttonDown ? 'icon-new-down-arrow-icon icon-inverse f-24' : 'icon-new-down-arrow-icon f-24'} />
            </button>
          </div>
        </div>

        {showPanel && (
          <div className="size-range-box">
            {isLoading ? (
              <div className="text-center">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <div>
                {(rangesData || []).length === 0 && (
                  <div className="size-range-empty">
                    <p className="f-14 text-grey mb-0">{t('SIZE_RANGE_MASTER.NO_RECORDS_FOUND')}</p>
                    {masterAddEditAccess && <span className="f-12 text-grey">{t('SIZE_RANGE_MASTER.NO_RECORDS_FOUND_HINT')}</span>}
                  </div>
                )}

                {(rangesData || []).map((range, rangeIndex) => {
                  const rangeKey = range?._id || `new_${rangeIndex}`;
                  const sizes = Array.isArray(range?.sizes) ? range.sizes : [];
                  const isExpanded = expandedRangeIds[rangeKey] ?? true;

                  return (
                    <div className="card border-none mb-2" key={rangeKey}>
                      <div className="card-header size-range-header d-flex align-items-center">
                        <button
                          type="button"
                          className="btn d-flex align-items-center p-0 mr-2"
                          onClick={() => handleDeleteRange(range, rangeIndex)}
                          disabled={!masterDeleteAccess || isSavingAll}
                          aria-label={t('COMMON_LABEL.DELETE')}
                        >
                          <i className="icon-Delete f-20" />
                        </button>

                        <div className="flex-grow-1 position-relative">
                          <input
                            type="text"
                            className="form-control size-range-title f-14 font-medium"
                            placeholder={t('SIZE_RANGE_MASTER.SIZE_RANGE_TITLE')}
                            value={range?.name ?? ''}
                            onChange={(e) => handleRangeNameChange(rangeIndex, e.target.value)}
                            disabled={!masterAddEditAccess || isSavingAll}
                            maxLength={SIZE_RANGE_NAME_LIMIT}
                          />
                          <small
                            className={`character-count-indicator position-absolute text-muted ${
                              (range?.name?.length ?? 0) >= SIZE_RANGE_NAME_LIMIT * 0.9 ? 'text-warning' : ''
                            }`}
                          >
                            {range?.name?.length ?? 0}/{SIZE_RANGE_NAME_LIMIT}
                          </small>
                        </div>

                        <button
                          type="button"
                          className="btn d-inline-flex p-0 ml-2"
                          onClick={() => toggleRange(rangeKey)}
                          disabled={isSavingAll}
                          aria-label={isExpanded ? t('COMMON_LABEL.COLLAPSE') : t('COMMON_LABEL.EXPAND')}
                        >
                          <i className={isExpanded ? 'icon-new-down-arrow-icon icon-inverse f-20' : 'icon-new-down-arrow-icon f-20'} />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="card-body size-range-list">
                          <ul>
                            {sizes.map((size, sizeIndex) => {
                              const sizeKey = size?._id || `new_${rangeKey}_${sizeIndex}`;
                              return (
                                <li className="size-range-item" key={sizeKey}>
                                  <div className="input-container flex-grow-1 position-relative">
                                    <input
                                      type="text"
                                      className={(size?.name?.length ?? 0) >= SIZE_NAME_LIMIT * 0.9 ? 'border-warning' : ''}
                                      value={size?.name ?? ''}
                                      onChange={(e) => handleSizeNameChange(rangeIndex, sizeIndex, e.target.value)}
                                      disabled={!masterAddEditAccess || !range?._id || isSavingAll}
                                      maxLength={SIZE_NAME_LIMIT}
                                    />
                                    <small
                                      className={`character-count-indicator position-absolute text-muted ${
                                        (size?.name?.length ?? 0) >= SIZE_NAME_LIMIT * 0.9 ? 'text-warning' : ''
                                      }`}
                                    >
                                      {size?.name?.length ?? 0}/{SIZE_NAME_LIMIT}
                                    </small>
                                  </div>
                                  <div className="input-action">
                                    {masterDeleteAccess && (
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteSize(range?._id, size?._id, rangeIndex, sizeIndex)}
                                        disabled={!masterDeleteAccess || !range?._id || isSavingAll}
                                        aria-label={t('COMMON_LABEL.DELETE')}
                                      >
                                        <span className="icon-Delete f-20 mt-3" />
                                      </button>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                          {masterAddEditAccess && (
                            <div className="d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn add-size-title p-0 mt-2 mr-8"
                                onClick={() => handleAddSize(rangeIndex)}
                                disabled={!masterAddEditAccess || !range?._id || isSavingAll}
                                aria-label={t('COMMON_LABEL.ADD')}
                              >
                                <span className="icon-Add text-grey f-18" />
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {rangesData?.length > 0 && (
                  <div className="d-flex justify-content-end mt-3 mr-8">
                    <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                      <button className="btn p-0" type="button" onClick={handleSaveAll} disabled={!masterAddEditAccess || isSavingAll} aria-label="Save">
                        {isSavingAll ? <Spinner animation="border" size="sm" /> : <i className="icon-Tick-1 f-24" />}
                      </button>
                    </CustomTooltip>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showDeleteRangeConfirm && (
        <ConfirmationPopup
          onOpen={showDeleteRangeConfirm}
          onConfirm={() => deleteRangeConfirmed()}
          title={t('SIZE_RANGE_MASTER.DELETE_RANGE_TITLE')}
          message={t('SIZE_RANGE_MASTER.DELETE_RANGE_MESSAGE')}
          toggle={() => setShowDeleteRangeConfirm(!showDeleteRangeConfirm)}
          loading={isLoadingConf}
        />
      )}

      {showDeleteSizeConfirm && (
        <ConfirmationPopup
          onOpen={showDeleteSizeConfirm}
          onConfirm={() => deleteSizeConfirmed()}
          title={t('SIZE_RANGE_MASTER.DELETE_SIZE_TITLE')}
          message={t('SIZE_RANGE_MASTER.DELETE_SIZE_MESSAGE')}
          toggle={() => setShowDeleteSizeConfirm(!showDeleteSizeConfirm)}
          loading={isLoadingConf}
        />
      )}
    </Accordion>
  );
}

export default POSizeMaster;
